import { Avi } from '../ui/Avi';
import { Spinner } from '../ui/Spinner';
import { darkTheme, FONT_MONO } from '../../theme/tokens';
import { MarkdownText } from './MarkdownText';
import { ToolPill } from './ToolPill';
import { ConfirmationFooter } from './ConfirmationFooter';
import { DiffCard } from './DiffCard';
import { StartedApplyingCard } from './StartedApplyingCard';
import type { AssistantItem, AssistantToolItem } from './groupTurns';
import { isAgentLooping, toSegments } from './chatAssistantSegments';
import { isApplyFindingOutput, isStartedApplyingOutput } from '../../types';

const t = darkTheme;

interface ChatAssistantBubbleProps {
  items: AssistantItem[];
  /** True when this bubble represents the live, growing stream. Drives the
   *  typing indicator across the two "agent is busy but not actively writing"
   *  gaps: before the first event arrives, and between a resolved tool pill
   *  and the next text/tool. */
  isLive?: boolean;
}

/**
 * One assistant "turn" in the timeline. The outer container holds the avatar
 * and EKOM label; inside, items render as an interleaved sequence:
 *   - consecutive text items coalesce into one styled speech bubble
 *   - each tool item renders as a muted activity row outside the bubble
 * Tool rows are intentionally not part of any speech bubble — they're the
 * agent's work log, not its words. The typing indicator pins to the end.
 */
export function ChatAssistantBubble({ items, isLive = false }: ChatAssistantBubbleProps) {
  const showTypingIndicator = isLive && isAgentLooping(items);
  const segments = toSegments(items);
  return (
    <div data-testid="chat-assistant-bubble" style={{ marginBottom: 16 }}>
      <div
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'flex-start',
        }}
      >
        <Avi size={32} />
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            // Subtle background tint groups the whole turn — speech bubbles AND
            // muted tool rows — under one EKOM label, so the interleaved
            // sequence reads as a coherent unit. Sits one shade lighter than
            // the page bg, one shade darker than the inner bubble surface, so
            // bubbles still pop within the tint. Hairline border crisps the
            // edge against the darker page bg.
            background: t.bgAlt,
            border: '1px solid rgba(255,255,255,.06)',
            borderRadius: 12,
            padding: '12px 14px',
          }}
          aria-live={isLive ? 'polite' : undefined}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontFamily: FONT_MONO,
              fontSize: 10,
              fontWeight: 500,
              color: t.accent,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              marginBottom: -2,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                fontSize: 7,
                // Pulse only while this turn is actively streaming. Historical
                // turns render a static dot so the eyebrow keeps its shape
                // without adding motion noise to every replayed assistant turn.
                animation: isLive ? 'livePulse 2.5s ease-in-out infinite' : 'none',
              }}
            >
              ◉
            </span>
            EKOM
          </div>
          {segments.map((segment) =>
            segment.kind === 'text' ? (
              <TextBubble key={segment.id} texts={segment.texts} />
            ) : (
              <ToolRow key={segment.id} item={segment.item} live={isLive} />
            ),
          )}
          {showTypingIndicator && (
            <div
              data-testid="chat-typing-indicator"
              aria-label="Chat is thinking"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                color: t.text3,
              }}
            >
              <Spinner size={14} />
              <span style={{ fontSize: 13 }}>Thinking…</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TextBubble({ texts }: { texts: string[] }) {
  return (
    <div
      data-testid="assistant-text-bubble"
      style={{
        background: t.surface,
        borderRadius: 12,
        padding: '14px 18px',
        color: t.text,
        fontSize: 14,
        lineHeight: 1.55,
      }}
    >
      {texts.map((text, i) => (
        <MarkdownText key={i} text={text} />
      ))}
    </div>
  );
}

function ToolRow({ item, live }: { item: AssistantToolItem; live: boolean }) {
  // Footer rendered only when the result carries an unresolved
  // ConfirmationRequest. Once a later turn supplies a tool_use with
  // matching finding_id + confirmed=true, `confirmationResolved`
  // suppresses the affordance on replay.
  const confirmation =
    item.confirmation && !item.confirmationResolved ? item.confirmation : null;
  const footer = confirmation ? (
    <>
      {/* Show what's being approved above the Confirm/Cancel buttons. */}
      {confirmation.diff && <DiffCard diff={confirmation.diff} />}
      <ConfirmationFooter
        findingId={confirmation.proposed_action.finding_id}
        summary={confirmation.summary}
        entityIds={confirmation.proposed_action.entity_ids}
        affectedCount={confirmation.proposed_action.affected_count}
      />
    </>
  ) : undefined;
  return (
    <>
      <ToolPill
        displayName={item.displayName}
        result={item.result}
        actionFooter={footer}
      />
      <ToolResultDiffs content={item.result?.content} live={live} />
    </>
  );
}

/**
 * The pre-apply snapshot card rendered below an apply_finding success row.
 * get_findings results surface a turn-level pointer to the Findings table
 * instead (SC-2755); the apply *confirmation* diff renders in the footer (above
 * Confirm/Cancel).
 */
const APPLY_RESULT_HEADER: Record<string, { text: string; color: string } | undefined> = {
  applied: { text: '✓ Applied', color: t.accentText },
  already_applied: { text: 'Already applied', color: t.text2 },
  no_match: { text: 'Nothing changed', color: t.text2 },
  drifted: { text: 'Nothing changed', color: t.text2 },
};

function ToolResultDiffs({ content, live }: { content?: Record<string, unknown> | string; live: boolean }) {
  // SC-2580 — an async whole-finding apply returns a "started applying" card (the
  // diff under an "Applying…" header), not the synchronous "applied" diff. Checked
  // first: both share `needs_confirmation: false` and discriminate on `outcome`.
  if (isStartedApplyingOutput(content)) {
    return <StartedApplyingCard findingId={content.finding_id} diff={content.diff} live={live} />;
  }
  if (isApplyFindingOutput(content)) {
    // SC-3476 — say what the apply did above the pre-apply snapshot, so the card reads
    // as done rather than as a proposal. The BE answers the same sync result for a
    // no-op, so the header follows `apply_result`; a row from before the BE sent it
    // gets no header (it may have been a no-op), like the card before SC-3476.
    const header = APPLY_RESULT_HEADER[content.apply_result ?? ''];
    return (
      <div data-testid="applied-chat-card" style={{ marginTop: 6 }}>
        {header && (
          <div role="status" style={{ fontSize: 12.5, fontWeight: 600, color: header.color }}>
            {header.text}
          </div>
        )}
        {content.diff && <DiffCard diff={content.diff} />}
      </div>
    );
  }
  return null;
}

